// src/components/clients/TimelineEvent.tsx
//
// Timeline row · icon + body + relative time + per-kind inline actions +
// expandable detail (PKT-0064 v1c).
//
// PKT-0043a Phase 1 shipped icon+body+timestamp only.
// PR #123 (Director #7 PKT-0043b recovery) added inline action chips
// (PDF · Resend · Edit · Delete) + TimelineEmailBadge inline + readable
// status_changed format.
// PKT-0064 v1c (this revision) layers expandable detail rows on top:
//   - Chevron toggle on rich kinds → MUI Collapse reveals per-kind detail
//   - list_sent          → property cards + personal message + Copy email
//   - guest_card_sent    → recipient + property snapshot + Copy email
//   - note               → full pre-wrapped content + Copy note
//   - email_sent activity → subject + EmailTrackingBadge + Copy subject
//   - Other kinds         → unchanged · no chevron
//
// All PR #123 behavior preserved verbatim:
//   - Per-kind inline actions (PDF · Resend · Edit · Delete) always visible
//   - TimelineEmailBadge inline on email-bearing rows (collapsed view)
//   - status_changed arrow rendering
//   - Invoice icon switching 🧾 / 💵 on paid
//   - Row hover background shift
//
// Refs:
//   - live/packets/open/PKT-0064-sidebar-timeline-v1c-expandable-rows.md
//   - live/handoffs/EVENT-0110-checkin-claim-worker-wc-agent-53-pkt-0064-sidebar-timeline-v1c.md
//   - projects/locatenowpro-client-story-redesign.md §E.4 + §E.6

import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  Collapse,
  Chip,
} from "@mui/material";
import {
  PictureAsPdf as PdfIcon,
  Send as SendIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  formatRelativeTime,
  getActivityIcon,
  getActivityColor,
  formatActivityDescription,
} from "./helpers";
import { statusConfig } from "./config";
import type { ClientStatus } from "./types";
import type { TimelineEvent } from "../../lib/timelineEvents";
import type { Invoice } from "./invoiceTypes";
import type { TrackingData } from "../../hooks/useEmailTracking";
import TimelineEmailBadge from "./TimelineEmailBadge";
import EmailTrackingBadge from "./EmailTrackingBadge";

interface TimelineEventProps {
  event: TimelineEvent;
  trackingMap?: Map<string, TrackingData>;
  onOpenInvoicePdf?: (invoice: Invoice) => void;
  onResendInvoice?: (invoice: Invoice) => void;
  onResendGuestCard?: (guestCard: any) => void;
  onOpenListingEmail?: (listing: any) => void;
  onOpenGuestCardEmail?: (guestCard: any) => void;
  onEditSearch?: (search: any) => void;
  onDeleteSearch?: (searchId: string) => void;
}

/**
 * Format status_changed activity bodies with readable arrow.
 * Activity metadata typically has { from_status, to_status } or just
 * { new_status }. Fall back to whatever's in body.
 */
const renderStatusChangeBody = (metadata: Record<string, any>): string => {
  const from = metadata.from_status || metadata.old_status;
  const to = metadata.to_status || metadata.new_status;
  const label = (s: string) =>
    statusConfig[s as ClientStatus]?.label || s.replace(/_/g, " ");
  // PKT-0916: a move made BY the invoice route carries its reason
  // ("… because INV-xxxx was marked paid") — show it, or it reads as a human move.
  const reason = typeof metadata.reason === "string" ? metadata.reason.trim() : "";
  const because = /\bbecause (.+)$/.exec(reason);
  const why = !reason ? "" : because ? ` · because ${because[1]}` : ` · ${reason}`;
  if (from && to) return `Status: ${label(from)} → ${label(to)}${why}`;
  if (to) return `Status changed to ${label(to)}${why}`;
  return "Status changed";
};

const renderNonActivity = (
  event: TimelineEvent
): { icon: string; color: string; body: string } => {
  const md = event.metadata || {};
  switch (event.kind) {
    case "note": {
      const preview = String(md.content || "").trim();
      const short =
        preview.length > 140 ? `${preview.substring(0, 140)}…` : preview;
      return { icon: "📝", color: "#795548", body: short || "Note added" };
    }
    case "list_sent": {
      const props = md.property_count || md.unit_count || 0;
      const recipient = md.recipient_email || "client";
      return {
        icon: "📤",
        color: "#3F51B5",
        body: `Sent list to ${recipient}${
          props ? ` · ${props} ${props === 1 ? "property" : "properties"}` : ""
        }${md.subject ? ` · "${md.subject}"` : ""}`,
      };
    }
    case "search_ran": {
      const name = md.name || "Untitled";
      const count = md.result_count ?? 0;
      return {
        icon: "🔍",
        color: "#9C27B0",
        body: `Saved search · "${name}" · ${count} ${
          count === 1 ? "result" : "results"
        }`,
      };
    }
    case "guest_card_sent": {
      const prop = md.property_name ? ` · ${md.property_name}` : "";
      // A downloaded card has no recipient — recipient_name is the
      // "Hand-submitted" placeholder, so the send wording read
      // "Sent guest card to Hand-submitted".
      if (md.delivery_method === "downloaded") {
        return {
          icon: "📋",
          color: "#673AB7",
          body: `Registered guest card by hand${prop} · no email sent`,
        };
      }
      const recipient = md.recipient_name || md.recipient_email || "recipient";
      return {
        icon: "📋",
        color: "#673AB7",
        body: `Sent guest card to ${recipient}${prop}`,
      };
    }
    case "invoice_sent": {
      const num = md.invoice_number ? `${md.invoice_number}` : "";
      const amount =
        typeof md.amount === "number" ? ` · $${md.amount.toFixed(2)}` : "";
      const prop = md.property_name ? ` · ${md.property_name}` : "";
      const status = md.status ? ` · ${md.status}` : "";
      return {
        icon: md.status === "paid" ? "💵" : "🧾",
        color: md.status === "paid" ? "#10B981" : "#FFC107",
        body: `Invoice ${num}${amount}${prop}${status}`.trim(),
      };
    }
    default:
      return { icon: "📌", color: "#757575", body: event.kind };
  }
};

/**
 * Decide whether a row gets a chevron + expandable detail block. Conservative ·
 * rows with no extra payload don't render a chevron (keeps the Timeline visually
 * quiet for sparse-data rows).
 */
const isExpandable = (event: TimelineEvent): boolean => {
  const md = event.metadata || {};
  switch (event.kind) {
    case "note": {
      const content = String(md.content || "").trim();
      // Only expandable when truncated · v1a slices at 140 chars
      return content.length > 140;
    }
    case "list_sent": {
      const props = (event.raw as any)?.properties;
      return (
        (Array.isArray(props) && props.length > 0) ||
        Boolean((event.raw as any)?.personal_message) ||
        Boolean(md.recipient_email)
      );
    }
    case "guest_card_sent": {
      return (
        Boolean(md.property_name) ||
        Boolean(md.recipient_name) ||
        Boolean(md.recipient_email)
      );
    }
    case "activity": {
      const subtype = event.subtype || "";
      if (subtype === "email_sent" || subtype === "emailed") {
        return Boolean(md.subject || md.tracking_id);
      }
      return false;
    }
    default:
      return false;
  }
};

const copyToClipboard = async (text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    // Older browsers or permission issues · graceful degrade
    // eslint-disable-next-line no-console
    console.warn("Clipboard write failed", err);
  }
};

const getTrackingBadge = (
  trackingId?: string,
  trackingData?: TrackingData
): {
  label: string;
  color: "success" | "warning" | "default" | "error";
  tooltip: string;
} => {
  if (!trackingId) {
    return {
      label: "Tracking off",
      color: "default",
      tooltip: "This send did not include open/click tracking.",
    };
  }

  if (!trackingData) {
    return {
      label: "Sent",
      color: "default",
      tooltip: "Tracking record is not loaded yet.",
    };
  }

  const nonBotClicks = trackingData.clicks.filter((click) => !click.suspected_bot);
  if (nonBotClicks.length > 0) {
    return {
      label: "Clicked",
      color: "success",
      tooltip: `Clicked ${formatRelativeTime(nonBotClicks[0].clicked_at || "")}`,
    };
  }

  if (
    trackingData.open_count > 1 &&
    trackingData.first_opened_at &&
    trackingData.last_opened_at
  ) {
    const first = new Date(trackingData.first_opened_at).getTime();
    const last = new Date(trackingData.last_opened_at).getTime();
    if (last - first > 120_000) {
      return {
        label: "Opened",
        color: "success",
        tooltip: `Opened ${trackingData.open_count} times, last ${formatRelativeTime(
          trackingData.last_opened_at
        )}`,
      };
    }
  }

  if (trackingData.open_count >= 1) {
    return {
      label: "Delivered",
      color: "warning",
      tooltip: `Pixel loaded ${formatRelativeTime(trackingData.first_opened_at || "")}.`,
    };
  }

  if (trackingData.sent_at) {
    const hoursSinceSend =
      (Date.now() - new Date(trackingData.sent_at).getTime()) / (1000 * 60 * 60);
    if (hoursSinceSend >= 24) {
      return {
        label: "Not opened",
        color: "error",
        tooltip: `Sent ${formatRelativeTime(trackingData.sent_at || "")}, no open recorded.`,
      };
    }
  }

  return {
    label: "Sent",
    color: "default",
    tooltip: `Sent ${formatRelativeTime(trackingData.sent_at || "")}`,
  };
};

const TrackingStatusChip: React.FC<{
  trackingId?: string;
  trackingMap?: Map<string, TrackingData>;
}> = ({ trackingId, trackingMap }) => {
  const badge = getTrackingBadge(
    trackingId,
    trackingId ? trackingMap?.get(trackingId) : undefined
  );
  return (
    <Tooltip title={badge.tooltip} arrow>
      <Chip
        label={badge.label}
        color={badge.color}
        size="small"
        variant="outlined"
        sx={{ height: 22, fontSize: "0.72rem" }}
      />
    </Tooltip>
  );
};

interface ExpandedDetailProps {
  event: TimelineEvent;
  trackingMap?: Map<string, TrackingData>;
  onOpenListingEmail?: (listing: any) => void;
  onOpenGuestCardEmail?: (guestCard: any) => void;
}

/**
 * Per-kind rich-detail panel rendered inside MUI Collapse when the row's
 * chevron is toggled open. Each kind decides its own layout. Hover quick
 * actions (Copy email / Copy subject / Copy note) render inside this block
 * so they only appear when the operator has chosen to expand.
 */
const ExpandedDetail: React.FC<ExpandedDetailProps> = ({
  event,
  trackingMap,
  onOpenListingEmail,
  onOpenGuestCardEmail,
}) => {
  const md = event.metadata || {};
  const raw = (event.raw as any) || {};

  if (event.kind === "note") {
    const fullContent = String(md.content || "").trim();
    return (
      <Box
        sx={{
          mt: 0.75,
          p: 1.25,
          bgcolor: "rgba(121,85,72,0.05)",
          borderLeft: "3px solid #795548",
          borderRadius: 1,
          position: "relative",
        }}
      >
        <Typography
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", color: "text.primary" }}
        >
          {fullContent}
        </Typography>
        <Box
          className="timeline-expanded-hover-actions"
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            opacity: 0,
            transition: "opacity 120ms ease",
          }}
        >
          <Tooltip title="Copy note">
            <IconButton
              size="small"
              onClick={() => copyToClipboard(fullContent)}
              sx={{ p: 0.25 }}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    );
  }

  if (event.kind === "list_sent") {
    const properties: any[] = Array.isArray(raw.properties)
      ? raw.properties
      : [];
    const personalMessage = String(raw.personal_message || "").trim();
    const trackingId = md.tracking_id ? String(md.tracking_id) : undefined;
    return (
      <Stack
        spacing={0.9}
        sx={{
          mt: 0.85,
          pl: 1.25,
          borderLeft: "2px solid rgba(99,102,241,0.35)",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Sent {formatRelativeTime(event.ts)}
            </Typography>
            <TrackingStatusChip trackingId={trackingId} trackingMap={trackingMap} />
          </Stack>
          {onOpenListingEmail && event.raw && (
            <Tooltip title="Preview email">
              <IconButton
                size="small"
                onClick={() => onOpenListingEmail(event.raw)}
                sx={{ p: 0.35, border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                <VisibilityIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        {personalMessage && (
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase" }}
            >
              Message
            </Typography>
            <Typography
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", mt: 0.25, lineHeight: 1.45 }}
            >
              {personalMessage}
            </Typography>
          </Box>
        )}
        {properties.length > 0 ? (
          <Stack spacing={0.5}>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase" }}
            >
              Properties ({properties.length})
            </Typography>
            {properties.slice(0, 5).map((prop: any, idx: number) => {
              const name =
                prop?.property_name ||
                prop?.name ||
                prop?.title ||
                prop?.unit_name ||
                "Property";
              const neighborhood =
                prop?.neighborhood || prop?.area || prop?.submarket;
              const units = Array.isArray(prop?.units) ? prop.units : [];
              const unitSummary =
                units.length > 0
                  ? `${units.length} ${units.length === 1 ? "unit" : "units"}`
                  : null;
              const firstUnit = units[0] || {};
              const price =
                prop?.price ??
                prop?.rent ??
                prop?.starting_price ??
                prop?.min_price ??
                firstUnit.current_price;
              const beds =
                prop?.bed_count ??
                prop?.beds ??
                prop?.bedrooms ??
                firstUnit.bedrooms;
              const priceStr =
                typeof price === "number"
                  ? `$${price.toLocaleString()}`
                  : typeof price === "string" && price
                  ? price
                  : null;
              const bedsStr =
                typeof beds === "number"
                  ? `${beds === 0 ? "Studio" : `${beds}BR`}`
                  : typeof beds === "string" && beds
                  ? beds
                  : null;
              return (
                <Box
                  key={prop?.id || prop?.unit_id || idx}
                  sx={{
                    py: 0.65,
                    borderTop: idx === 0 ? 0 : "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 650, lineHeight: 1.3 }}
                  >
                    {name}
                  </Typography>
                  {(neighborhood || unitSummary || priceStr || bedsStr) && (
                    <Stack
                      direction="row"
                      spacing={0.75}
                      sx={{ mt: 0.25, color: "text.secondary", flexWrap: "wrap" }}
                    >
                      {neighborhood && (
                        <Typography variant="caption">{neighborhood}</Typography>
                      )}
                      {unitSummary && (
                        <Typography variant="caption">· {unitSummary}</Typography>
                      )}
                      {priceStr && (
                        <Typography variant="caption">· {priceStr}</Typography>
                      )}
                      {bedsStr && (
                        <Typography variant="caption">· {bedsStr}</Typography>
                      )}
                    </Stack>
                  )}
                </Box>
              );
            })}
            {properties.length > 5 && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", pl: 0.75 }}
              >
                + {properties.length - 5} more
              </Typography>
            )}
          </Stack>
        ) : (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", pl: 0.5 }}
          >
            Property snapshot not captured in this send.
          </Typography>
        )}
      </Stack>
    );
  }

  if (event.kind === "guest_card_sent") {
    const propertyName = md.property_name ? String(md.property_name) : "";
    const neighborhood = md.neighborhood ? String(md.neighborhood) : "";
    const trackingId = md.tracking_id ? String(md.tracking_id) : undefined;
    return (
      <Box sx={{ mt: 0.75, position: "relative" }}>
        <Box
          sx={{
            p: 1,
            borderRadius: 1,
            bgcolor: "rgba(103,58,183,0.04)",
            border: "1px solid rgba(103,58,183,0.12)",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: propertyName || neighborhood ? 0.75 : 0 }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {md.delivery_method === "downloaded" ? "Registered" : "Sent"}{" "}
                {formatRelativeTime(event.ts)}
              </Typography>
              <TrackingStatusChip trackingId={trackingId} trackingMap={trackingMap} />
            </Stack>
            {onOpenGuestCardEmail && event.raw && (
              <Tooltip title="Preview guest card">
                <IconButton
                  size="small"
                  onClick={() => onOpenGuestCardEmail(event.raw)}
                  sx={{ p: 0.35 }}
                >
                  <VisibilityIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          {(propertyName || neighborhood) && (
            <Typography
              variant="body2"
              sx={{ mt: 0.25, fontWeight: 500, lineHeight: 1.3 }}
            >
              {propertyName || "Property"}
              {neighborhood ? ` · ${neighborhood}` : ""}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  if (event.kind === "activity") {
    // email_sent / emailed expansion · subject + tracking badge
    const subject = md.subject ? String(md.subject) : "";
    const trackingId = md.tracking_id
      ? String(md.tracking_id)
      : undefined;
    const trackingData = trackingId
      ? trackingMap?.get(trackingId)
      : undefined;
    return (
      <Stack spacing={0.75} sx={{ mt: 0.75, position: "relative" }}>
        {trackingData && (
          <Box>
            <EmailTrackingBadge trackingData={trackingData} />
          </Box>
        )}
        {subject && (
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: "rgba(0,188,212,0.05)",
              borderLeft: "3px solid #00BCD4",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 600 }}
            >
              Subject
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.25 }}>
              {subject}
            </Typography>
          </Box>
        )}
        {subject && (
          <Box
            className="timeline-expanded-hover-actions"
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              opacity: 0,
              transition: "opacity 120ms ease",
            }}
          >
            <Tooltip title="Copy subject">
              <Chip
                size="small"
                label="Copy subject"
                icon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                onClick={() => copyToClipboard(subject)}
                sx={{
                  height: 22,
                  fontSize: "0.7rem",
                  bgcolor: "background.paper",
                  border: "1px solid rgba(0,0,0,0.08)",
                  "& .MuiChip-icon": { ml: 0.5, mr: -0.25 },
                  "&:hover": { bgcolor: "action.hover" },
                }}
              />
            </Tooltip>
          </Box>
        )}
      </Stack>
    );
  }

  return null;
};

const TimelineEventRow: React.FC<TimelineEventProps> = ({
  event,
  trackingMap,
  onOpenInvoicePdf,
  onResendInvoice,
  onResendGuestCard,
  onOpenListingEmail,
  onOpenGuestCardEmail,
  onEditSearch,
  onDeleteSearch,
}) => {
  const [expanded, setExpanded] = useState(false);

  let icon: string;
  let color: string;
  let body: string;

  if (event.kind === "activity") {
    const subtype = event.subtype || "";
    icon = getActivityIcon(subtype);
    color = getActivityColor(subtype);
    if (
      subtype === "status_changed" ||
      subtype === "status_change" ||
      subtype === "client_status_changed"
    ) {
      body = renderStatusChangeBody(event.metadata || {});
    } else {
      body = formatActivityDescription(subtype, event.metadata || {});
    }
  } else {
    ({ icon, color, body } = renderNonActivity(event));
  }

  // Per-kind inline actions
  const md = event.metadata || {};
  const trackingId = md.tracking_id as string | undefined;

  const showEmailBadge =
    event.kind === "list_sent" || event.kind === "guest_card_sent";

  const showInvoiceActions =
    event.kind === "invoice_sent" && Boolean(event.raw);

  const showGuestCardResend =
    event.kind === "guest_card_sent" && Boolean(event.raw);

  const showSearchActions =
    event.kind === "search_ran" && Boolean(event.raw);

  const expandable = isExpandable(event);
  const toggleExpanded = () => {
    if (!expandable) return;
    setExpanded((v) => !v);
  };

  return (
    <Box
      sx={{
        py: 1.25,
        px: 1.25,
        borderRadius: 1.5,
        transition: "background-color 120ms ease",
        "&:hover": {
          backgroundColor: "rgba(0,0,0,0.025)",
          "& .timeline-expanded-hover-actions": { opacity: 1 },
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="flex-start"
        onClick={toggleExpanded}
        role={expandable ? "button" : undefined}
        aria-expanded={expandable ? expanded : undefined}
        tabIndex={expandable ? 0 : undefined}
        onKeyDown={(event) => {
          if (!expandable) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleExpanded();
          }
        }}
        sx={{ cursor: expandable ? "pointer" : "default" }}
      >
        <Avatar
          sx={{
            bgcolor: color,
            width: 30,
            height: 30,
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            rowGap={0.5}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.primary",
                lineHeight: 1.4,
                wordBreak: "break-word",
                flex: 1,
                minWidth: 0,
              }}
            >
              {body}
            </Typography>
            {showEmailBadge && (
              trackingId ? (
                <TimelineEmailBadge
                  trackingId={trackingId}
                  trackingMap={trackingMap}
                />
              ) : null
            )}
            {expandable && (
              <Tooltip title={expanded ? "Collapse" : "Expand details"}>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleExpanded();
                  }}
                  aria-label={expanded ? "Collapse" : "Expand details"}
                  sx={{
                    p: 0.25,
                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 160ms ease",
                  }}
                >
                  <ExpandMoreIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mt: 0.25 }}
          >
            {formatRelativeTime(event.ts)}
          </Typography>

          {(showInvoiceActions || showGuestCardResend || showSearchActions) && (
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ mt: 0.75 }}
              alignItems="center"
              onClick={(event) => event.stopPropagation()}
            >
              {showInvoiceActions && onOpenInvoicePdf && (
                <Button
                  size="small"
                  startIcon={<PdfIcon fontSize="small" />}
                  onClick={() =>
                    onOpenInvoicePdf(event.raw as Invoice)
                  }
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    py: 0.25,
                    minWidth: 0,
                  }}
                >
                  View PDF
                </Button>
              )}
              {showInvoiceActions && onResendInvoice && (
                <Button
                  size="small"
                  startIcon={<SendIcon fontSize="small" />}
                  onClick={() =>
                    onResendInvoice(event.raw as Invoice)
                  }
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    py: 0.25,
                    minWidth: 0,
                  }}
                >
                  Resend
                </Button>
              )}
              {showGuestCardResend && onResendGuestCard && (
                <Button
                  size="small"
                  startIcon={<SendIcon fontSize="small" />}
                  onClick={() => onResendGuestCard(event.raw)}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    py: 0.25,
                    minWidth: 0,
                  }}
                >
                  Resend
                </Button>
              )}
              {showSearchActions && onEditSearch && (
                <Tooltip title="Edit saved search">
                  <IconButton
                    size="small"
                    onClick={() => onEditSearch(event.raw)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {showSearchActions && onDeleteSearch && (
                <Tooltip title="Delete saved search">
                  <IconButton
                    size="small"
                    onClick={() => onDeleteSearch((event.raw as any).id)}
                    sx={{ color: "error.main" }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )}
        </Box>
      </Stack>
      {expandable && (
        <Box sx={{ pl: 5.6 }} onClick={(event) => event.stopPropagation()}>
          <Collapse in={expanded} timeout={160} unmountOnExit>
            <ExpandedDetail
              event={event}
              trackingMap={trackingMap}
              onOpenListingEmail={onOpenListingEmail}
              onOpenGuestCardEmail={onOpenGuestCardEmail}
            />
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default TimelineEventRow;
